import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Download, Loader2, Plus, Search, StickyNote, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentUser } from '../../hook_au/useCurrentUser';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { LookupAutocomplete } from '../ui/lookup-autocomplete';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useOrdersApi, useOrderActions } from '../../services/orders';
import { useShippingLocationsApi } from '../../services/shippingLocations';
import { useSuppliersApi } from '../../services/suppliers';
import { useAppSettings } from '../../hook/useAppSettings';
import type { Order, OrderStatus } from '../../types/orders';
import { OrderEmailDialog } from '../orders/OrderEmailDialog';
import { SubprojectLink } from '../orders/SubprojectLink';

const STATUS_LABELS: Record<OrderStatus, string> = {
  borrador: 'Borrador',
  pedido: 'Pedido',
  confirmado: 'Confirmado',
  enviado: 'Enviado',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
};

const STATUS_VARIANTS: Record<OrderStatus, 'secondary' | 'inProgress' | 'quoted' | 'finished' | 'destructive'> = {
  borrador: 'secondary',
  pedido: 'quoted',
  confirmado: 'inProgress',
  enviado: 'inProgress',
  recibido: 'finished',
  cancelado: 'destructive',
};

const COLUMN_WIDTHS = {
  orderRef: '8%',
  project: '12%',
  supplier: '8%',
  items: '5%',
  total: '6%',
  status: '7%',
  supplierEmailed: '7%',
  supplierCalled: '7%',
  supplierConfirmed: '7%',
  shipping: '10%',
  tracking: '9%',
  delivery: '7%',
  notes: '6%',
  pdf: '6%',
  actions: '7%',
} as const;

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const formatOrderRef = (order: Order) => order.order_number || '—';

export function Orders() {
  const { role } = useCurrentUser();
  const { settings: appSettings, formatCurrency: formatMoney } = useAppSettings();
  const isLogistica = role === 'logistica';
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [calledFilter, setCalledFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [confirmDelete, setConfirmDelete] = useState<Order | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderPendingCall, setOrderPendingCall] = useState<Order | null>(null);
  const [orderPendingEmail, setOrderPendingEmail] = useState<Order | null>(null);
  const [sendingOrderEmail, setSendingOrderEmail] = useState(false);
  const [markingSupplierCalled, setMarkingSupplierCalled] = useState(false);
  const [orderPendingSupplierConfirmation, setOrderPendingSupplierConfirmation] = useState<Order | null>(null);
  const [markingSupplierConfirmed, setMarkingSupplierConfirmed] = useState(false);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const [detailForm, setDetailForm] = useState({
  status: 'borrador' as OrderStatus,
  shipping_location_name: '',
  shipping_location_id: '',
  expected_delivery_date: '',
  order_number: '',
  tracking_number: '',
});
  const [savingDetail, setSavingDetail] = useState(false);

  const [notesPreview, setNotesPreview] = useState<Order | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState('1');

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(appSettings.currencyLocale);
  };

  const formatCurrency = (value?: string | number | null) => formatMoney(value);

  const {
    data: orders,
    loading,
    error,
    pagination,
    setFilters,
    setPagination,
    goToPage,
    refresh,
  } = useOrdersApi({}, { page: 1, page_size: 10 });

  const { data: suppliers } = useSuppliersApi({ search: undefined, active: undefined }, { page: 1, page_size: 100 });
  const {
    shippingLocations,
    loading: shippingLocationsLoading,
    refresh: refreshShippingLocations,
  } = useShippingLocationsApi({ active: true, limit: 200 });
  const { deleteOrder, updateOrder, downloadOrderPdf, sendOrderEmail } = useOrderActions();

  const openNotesPreview = (order: Order) => {
  setNotesPreview(order);
  setNoteDraft(order.notes ?? '');
};

  const closeNotesPreview = () => {
  if (savingNote) return;
  setNotesPreview(null);
  setNoteDraft('');
};

const handleSaveNote = async () => {
  if (!notesPreview) return;
  setSavingNote(true);
  try {
    await updateOrder(notesPreview.id, { notes: noteDraft.trim() || null });
    toast.success('Notas actualizadas');
    await refresh();
    closeNotesPreview();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No pudimos guardar la nota';
    toast.error(message);
  } finally {
    setSavingNote(false);
  }
};

  useEffect(() => {
    setFilters((prev) => {
      const next = {
        ...prev,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        supplier: supplierFilter === 'all' ? undefined : supplierFilter,
        supplier_called: calledFilter === 'all' ? undefined : calledFilter === 'yes',
      };
      return prev === next ? prev : next;
    });
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch, statusFilter, supplierFilter, calledFilter, setFilters, setPagination]);

  useEffect(() => {
  if (!detailOrder) return;
  setDetailForm({
  status: detailOrder.status,
  shipping_location_name: detailOrder.shipping_location ?? '',
  shipping_location_id: detailOrder.shipping_location_id ?? '',
  expected_delivery_date: detailOrder.expected_delivery_date ?? '',
  order_number: detailOrder.order_number ?? '',
  tracking_number: detailOrder.tracking_number ?? '',
});
  }, [detailOrder]);
const handleDetailSave = async () => {
  if (!detailOrder) return;

  const nextStatus = detailForm.status;
  const isStatusChanging = nextStatus !== detailOrder.status;
  const isShippingStatus = nextStatus === 'enviado' || nextStatus === 'recibido';
  const canMoveToShippingStatus = detailOrder.supplier_called && detailOrder.supplier_confirmed;

  if (isStatusChanging && !['enviado', 'recibido', 'cancelado'].includes(nextStatus)) {
    toast.error('Desde este detalle solo puedes cambiar el estado a enviado, recibido o cancelado.');
    return;
  }

  if (isShippingStatus && !canMoveToShippingStatus) {
    toast.error('Para poner el pedido en enviado o recibido, primero registra llamada y confirmación.');
    return;
  }
  if (detailForm.shipping_location_name.trim() && !detailForm.shipping_location_id) {
    toast.error('Selecciona un lugar de envío válido.');
    return;
  }

  setSavingDetail(true);
  try {
    await updateOrder(detailOrder.id, {
      status: nextStatus,
      shipping_location_id: detailForm.shipping_location_id || null,
      expected_delivery_date: detailForm.expected_delivery_date || null,
      order_number: detailForm.order_number.trim(),
      tracking_number: detailForm.tracking_number.trim() || null,
    });
    toast.success('Pedido actualizado');
    await refresh();
    setDetailOrder(null);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No pudimos actualizar el pedido';
    toast.error(message);
  } finally {
    setSavingDetail(false);
  }
};

  const supplierNames = useMemo(() => {
    const map: Record<string, string> = {};
    suppliers.forEach((supplier) => {
      map[supplier.id] = supplier.name;
    });
    return map;
  }, [suppliers]);
  const shippingLocationOptions = useMemo(
    () =>
      shippingLocations.map((shippingLocation) => ({
        label: shippingLocation.name,
        value: shippingLocation.id,
      })),
    [shippingLocations],
  );

  const uniqueSuppliers = useMemo(
  () =>
    suppliers
      .map((supplier) => ({ id: supplier.id, name: supplier.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
  [suppliers],
);

  const titleCount = pagination.total ?? orders.length;
  const totalPages = Math.max(
    1,
    pagination.total_pages ?? Math.ceil((pagination.total ?? orders.length) / pagination.page_size),
  );
  const canMoveDetailOrderToShippingStatus = Boolean(
    detailOrder?.supplier_called && detailOrder?.supplier_confirmed,
  );

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const commitPageInput = () => {
    const parsed = Number(pageInput);
    if (!Number.isInteger(parsed)) {
      setPageInput(String(pagination.page));
      return;
    }
    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    if (nextPage !== pagination.page) void goToPage(nextPage);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSubmitting(true);
    try {
      await deleteOrder(confirmDelete.id);
      toast.success('Pedido eliminado');
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos eliminar el pedido';
      toast.error(message);
    } finally {
      setSubmitting(false);
      setConfirmDelete(null);
    }
  };

  const handleMarkSupplierCalled = async () => {
    if (!orderPendingCall) return;
    const shippingLocationId = orderPendingCall.shipping_location_id;
    if (!shippingLocationId) {
      toast.error('Añade un lugar de envío antes de registrar la llamada.');
      setOrderPendingCall(null);
      return;
    }
    setMarkingSupplierCalled(true);
    try {
      await updateOrder(orderPendingCall.id, {
        supplier_called: true,
        shipping_location_id: shippingLocationId,
        status: 'pedido',
      });
      toast.success('Llamada registrada');
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos registrar la llamada';
      toast.error(message);
    } finally {
      setMarkingSupplierCalled(false);
      setOrderPendingCall(null);
    }
  };

  const openOrderEmailDialog = (order: Order) => {
    if (!order.shipping_location_id) {
      toast.error('Añade un lugar de envío antes de enviar el pedido por email.');
      return;
    }
    if (!order.supplier_email) {
      toast.error('El proveedor no tiene email configurado. Actualízalo en Proveedores.');
      return;
    }
    setOrderPendingEmail(order);
  };

  const handleSendOrderEmail = async (payload: { to_email: string; subject: string; body: string; extra_attachments?: File[] }) => {
    if (!orderPendingEmail) return;
    setSendingOrderEmail(true);
    try {
      await sendOrderEmail(orderPendingEmail.id, payload);
      toast.success('Email enviado y pedido marcado.');
      await refresh();
      setOrderPendingEmail(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos enviar el email del pedido';
      toast.error(message);
    } finally {
      setSendingOrderEmail(false);
    }
  };

  const handleMarkSupplierConfirmed = async () => {
    if (!orderPendingSupplierConfirmation) return;
    if (!orderPendingSupplierConfirmation.supplier_called) {
      toast.error('Debes registrar la llamada antes de confirmar el pedido con el proveedor.');
      setOrderPendingSupplierConfirmation(null);
      return;
    }
    setMarkingSupplierConfirmed(true);
    try {
      await updateOrder(orderPendingSupplierConfirmation.id, {
        supplier_confirmed: true,
        status: 'confirmado',
      });
      toast.success('Confirmación registrada');
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos registrar la confirmación';
      toast.error(message);
    } finally {
      setMarkingSupplierConfirmed(false);
      setOrderPendingSupplierConfirmation(null);
    }
  };

  const handleDownloadPdf = async (order: Order) => {
    setDownloadingPdfId(order.id);
    try {
      const blob = await downloadOrderPdf(order.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido-${order.order_number || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos generar el PDF';
      toast.error(message);
    } finally {
      setDownloadingPdfId(null);
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-2xl text-gray-900">Gestión de Pedidos</h1>
          <p className="text-gray-600">Control de pedidos y entregas de proveedores</p>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nº pedido, nº seguimiento, proyecto o proveedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)} disabled={loading}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
                <SelectItem value="pedido">Pedido</SelectItem>
                <SelectItem value="confirmado">Confirmado</SelectItem>
                <SelectItem value="enviado">Enviado</SelectItem>
                <SelectItem value="recibido">Recibido</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={supplierFilter} onValueChange={setSupplierFilter} disabled={loading}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {uniqueSuppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id} className="line-clamp-2">
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={calledFilter} onValueChange={(v) => setCalledFilter(v as typeof calledFilter)} disabled={loading}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="¿Has llamado?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="yes">Llamados</SelectItem>
                <SelectItem value="no">Sin llamar</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setSupplierFilter('all');
                setCalledFilter('all');
              }}
              disabled={loading}
            >
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Pedidos {loading ? '(cargando...)' : `(${titleCount})`}</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 p-4 text-sm text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
              <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
                Reintentar
              </Button>
            </div>
          ) : null}

          <Table className="table-fixed w-full [&_th]:box-border [&_td]:box-border">
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: COLUMN_WIDTHS.orderRef }} className="!whitespace-normal break-words text-left">
                    Nº Pedido
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.project }} className="!whitespace-normal break-words text-left">
                    Proyecto
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.supplier }} className="!whitespace-normal break-words text-left">
                    Proveedor
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.items }} className="text-center">
                    Items
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.total }} className="text-center">
                    Total
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.status }} className="text-center">
                    Estado
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.supplierEmailed }} className="text-center">
                    Email
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.supplierCalled }} className="text-center">
                    ¿Has llamado?
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.supplierConfirmed }} className="text-center">
                    Confirmación
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.shipping }} className="!whitespace-normal break-words text-center">
                    Lugar de envío
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.tracking }} className="!whitespace-normal break-words text-center">
                    Nº seguimiento
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.delivery }} className="text-center">
                    Entrega prevista
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.notes }} className="text-center">
                    Notas
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.pdf }} className="text-center">
                    PDF
                  </TableHead>
                  <TableHead style={{ width: COLUMN_WIDTHS.actions }} className="text-center !whitespace-normal break-words">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ width: COLUMN_WIDTHS.orderRef }}><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.project }}><Skeleton className="h-4 w-36" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplier }}><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.items }} className="text-center"><Skeleton className="mx-auto h-5 w-12" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.total }} className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.status }} className="text-center"><Skeleton className="mx-auto h-5 w-20" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierEmailed }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierCalled }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierConfirmed }} className="text-center"><Skeleton className="mx-auto h-5 w-14" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.shipping }}><Skeleton className="h-4 w-36" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.tracking }} className="text-center"><Skeleton className="mx-auto h-5 w-24" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.delivery }} className="text-center"><Skeleton className="mx-auto h-5 w-20" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.notes }} className="text-center"><Skeleton className="mx-auto h-5 w-20" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.pdf }} className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.actions }} className="text-center"><Skeleton className="mx-auto h-5 w-28" /></TableCell>
                    </TableRow>
                  ))
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="py-8 text-center text-gray-500">
                      No hay pedidos con los filtros actuales.
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-gray-50">
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.orderRef }}
                        className="!whitespace-normal break-words text-left text-blue-600 align-top"
                      >
                        {formatOrderRef(order)}
                      </TableCell>
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.project }}
                        className="!whitespace-normal break-words text-left text-gray-900 align-top"
                      >
                        <div className="flex flex-wrap items-center gap-1">
                          <span>{order.project_name ?? '—'}</span>
                          <span>/</span>
                          <SubprojectLink
                            projectId={order.project_id}
                            subprojectId={order.subproject_id}
                            label={order.subproject_name ?? '—'}
                            canNavigate={!isLogistica}
                            disabledTitle={isLogistica ? 'Sin acceso al subproyecto' : undefined}
                            textClassName="text-gray-900"
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.supplier }}
                        className="!whitespace-normal break-words text-left text-gray-600 align-top"
                      >
                        {order.supplier_name ?? supplierNames[order.supplier_id] ?? order.supplier_id}
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.items }} className="text-center align-top">
                        <Badge variant="outline" className="px-2 py-1 text-xs">
                          {order.items?.length ?? 0}
                        </Badge>
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.total }} className="text-center text-gray-900 align-top">
                        {formatCurrency(order.total_cost)}
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.status }} className="text-center align-top">
                        <Badge variant={STATUS_VARIANTS[order.status]} className="px-2 py-1">
                          {STATUS_LABELS[order.status]}
                        </Badge>
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierEmailed }} className="text-center align-top">
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={
                            order.supplier_emailed ||
                            sendingOrderEmail ||
                            markingSupplierCalled ||
                            markingSupplierConfirmed
                          }
                          onClick={() => openOrderEmailDialog(order)}
                          aria-label={order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}
                          title={order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}
                        >
                          {order.supplier_emailed ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-red-500" />
                          )}
                          <span className="sr-only">{order.supplier_emailed ? 'Email enviado' : 'Email pendiente'}</span>
                        </Button>
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierCalled }} className="text-center align-top">
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={
                            order.supplier_called ||
                            markingSupplierCalled ||
                            markingSupplierConfirmed ||
                            !order.shipping_location_id
                          }
                          onClick={() => setOrderPendingCall(order)}
                          aria-label={order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}
                          title={order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}
                        >
                          {order.supplier_called ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-red-500" />
                          )}
                          <span className="sr-only">{order.supplier_called ? 'Llamada registrada' : 'Sin llamada'}</span>
                        </Button>
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.supplierConfirmed }} className="text-center align-top">
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={
                            order.supplier_confirmed ||
                            markingSupplierConfirmed ||
                            markingSupplierCalled ||
                            !order.supplier_called
                          }
                          onClick={() => setOrderPendingSupplierConfirmation(order)}
                          aria-label={order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                          title={order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                        >
                          {order.supplier_confirmed ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-red-500" />
                          )}
                          <span className="sr-only">
                            {order.supplier_confirmed ? 'Pedido confirmado con proveedor' : 'Sin confirmar'}
                          </span>
                        </Button>
                      </TableCell>
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.shipping }}
                        className="!whitespace-normal break-words text-center text-gray-700 align-top"
                      >
                        {order.shipping_location ?? '—'}
                      </TableCell>
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.tracking }}
                        className="!whitespace-normal break-words text-center text-gray-700 align-top"
                      >
                        {order.tracking_number ?? '—'}
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.delivery }} className="text-center text-gray-700 align-top">
                        {formatDate(order.expected_delivery_date)}
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.notes }} className="text-center align-top">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`inline-flex items-center gap-1 border px-2 py-1 ${
                            order.notes?.trim()
                              ? 'border-orange-300 bg-orange-50 text-orange-600'
                              : 'border-gray-200 bg-gray-50 text-gray-500'
                          }`}
                          title={order.notes ?? 'Sin notas'}
                          onClick={() => openNotesPreview(order)}
                        >
                          <StickyNote className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell style={{ width: COLUMN_WIDTHS.pdf }} className="text-center align-top">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="inline-flex items-center gap-1 border px-2 py-1 text-gray-700"
                          onClick={() => handleDownloadPdf(order)}
                          disabled={downloadingPdfId === order.id || loading}
                          title="Descargar PDF del pedido"
                        >
                          {downloadingPdfId === order.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell
                        style={{ width: COLUMN_WIDTHS.actions }}
                        className="!whitespace-normal break-words text-center align-top"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full whitespace-normal break-words leading-tight"
                            onClick={() => setDetailOrder(order)}
                          >
                            Ver detalle
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full whitespace-normal break-words leading-tight text-red-600 hover:text-red-700"
                            onClick={() => setConfirmDelete(order)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1 || loading}>
              Anterior
            </Button>
            <div className="flex items-center gap-2">
              <Input
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitPageInput();
                }}
                className="h-9 w-16 text-center"
                disabled={loading}
              />
              <span className="text-sm text-gray-600">/ {totalPages}</span>
            </div>
                        <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(pagination.page + 1)}
              disabled={loading || pagination.page >= totalPages}
            >
              Siguiente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(totalPages)}
              disabled={loading || pagination.page >= totalPages}
            >
              Última página
            </Button>
            <Select
              value={String(pagination.page_size)}
              onValueChange={(value) => {
                const nextSize = Number(value);
                setPagination((prev) => ({ ...prev, page: 1, page_size: nextSize }));
              }}
              disabled={loading}
            >
              <SelectTrigger className="w-24">
                <SelectValue placeholder={String(pagination.page_size)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-gray-500" /> : null}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pedido?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-gray-700">
              <p>Eliminarás definitivamente el pedido {confirmDelete ? formatOrderRef(confirmDelete) : ''}.</p>
              <p>Esta acción no se puede deshacer.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={submitting} onClick={handleDelete}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={Boolean(orderPendingCall)} onOpenChange={(open) => { if (!open) setOrderPendingCall(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Registrar llamada al proveedor?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-gray-700">
              <p>¿Has llamado al proveedor del pedido {orderPendingCall ? formatOrderRef(orderPendingCall) : ''}?</p>
              <p>Esta acción marcará el pedido como "pedido".</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markingSupplierCalled}>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-green-600 hover:bg-green-700" disabled={markingSupplierCalled} onClick={handleMarkSupplierCalled}>
              {markingSupplierCalled ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Registrar llamada
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={Boolean(orderPendingSupplierConfirmation)}
        onOpenChange={(open) => { if (!open) setOrderPendingSupplierConfirmation(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Registrar confirmación con el proveedor?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-gray-700">
              <p>
                ¿Has confirmado con el proveedor el pedido{' '}
                {orderPendingSupplierConfirmation ? formatOrderRef(orderPendingSupplierConfirmation) : ''}?
              </p>
              <p>Esta acción marcará el pedido como "confirmado".</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markingSupplierConfirmed}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              disabled={markingSupplierConfirmed}
              onClick={handleMarkSupplierConfirmed}
            >
              {markingSupplierConfirmed ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Registrar confirmación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={Boolean(detailOrder)} onOpenChange={(open) => { if (!open && !savingDetail) setDetailOrder(null); }}>
  <DialogContent className="w-full min-w-[1000px] max-w-5xl max-h-[85vh] overflow-y-auto">
  <DialogHeader>
    <DialogTitle>Detalle del pedido {detailOrder ? formatOrderRef(detailOrder) : ''}</DialogTitle>
    <DialogDescription>
      {detailOrder
        ? `${detailOrder.project_name ?? '—'} / ${detailOrder.subproject_name ?? '—'} · ${
            detailOrder.supplier_name ?? supplierNames[detailOrder.supplier_id] ?? '—'
          }`
        : ''}
    </DialogDescription>
  </DialogHeader>

  <div className="grid gap-4">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <div className="grid gap-2">
    <label className="text-sm font-medium text-gray-700">Nombre del pedido</label>
    <Input
      value={detailForm.order_number}
      onChange={(event) => setDetailForm((prev) => ({ ...prev, order_number: event.target.value }))}
      placeholder="Ej: PED-1234 o tu referencia interna"
    />
  </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">Número de seguimiento</label>
        <Input
          value={detailForm.tracking_number}
          onChange={(event) => setDetailForm((prev) => ({ ...prev, tracking_number: event.target.value }))}
          placeholder="Opcional"
        />
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">Estado</label>
        <Select
          value={detailForm.status}
          onValueChange={(value) => setDetailForm((prev) => ({ ...prev, status: value as OrderStatus }))}
        >
          <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="borrador" disabled>Borrador</SelectItem>
            <SelectItem value="pedido" disabled>Pedido</SelectItem>
            <SelectItem value="confirmado" disabled>Confirmado</SelectItem>
            <SelectItem value="enviado" disabled={!canMoveDetailOrderToShippingStatus}>Enviado</SelectItem>
            <SelectItem value="recibido" disabled={!canMoveDetailOrderToShippingStatus}>Recibido</SelectItem>
            <SelectItem value="cancelado">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">Lugar de envío</label>
        <LookupAutocomplete
          value={detailForm.shipping_location_name}
          options={shippingLocationOptions}
          loading={shippingLocationsLoading}
          placeholder="Busca y selecciona un lugar de envío"
          onSearch={(query) => {
            void refreshShippingLocations({
              search: query.trim() || undefined,
              active: true,
              limit: 200,
            });
          }}
          onTextChange={(next) =>
            setDetailForm((prev) => ({
              ...prev,
              shipping_location_name: next,
              shipping_location_id: '',
            }))
          }
          onSelect={({ label, value }) =>
            setDetailForm((prev) => ({
              ...prev,
              shipping_location_name: label,
              shipping_location_id: value,
            }))
          }
        />
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">Entrega prevista</label>
        <Input
          type="date"
          value={detailForm.expected_delivery_date}
          onChange={(event) => setDetailForm((prev) => ({ ...prev, expected_delivery_date: event.target.value }))}
        />
      </div>
    </div>

    <div className="rounded-lg border">
      <Table className="table-fixed min-w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="!whitespace-normal break-words">Descripción</TableHead>
            <TableHead className="text-center whitespace-nowrap">Cantidad</TableHead>
            <TableHead className="text-center whitespace-nowrap">Coste unitario</TableHead>
            <TableHead className="text-center whitespace-nowrap">Total línea</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {detailOrder?.items?.length ? (
            detailOrder.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-gray-900 whitespace-normal break-words">{item.description}</TableCell>
                <TableCell className="text-center whitespace-nowrap">{item.quantity}</TableCell>
                <TableCell className="text-center whitespace-nowrap">{formatCurrency(item.unit_cost)}</TableCell>
                <TableCell className="text-center whitespace-nowrap">{formatCurrency(item.line_total_cost)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="py-6 text-center text-gray-500">
                Este pedido aún no tiene líneas.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </div>

  <DialogFooter>
    <Button variant="outline" onClick={() => setDetailOrder(null)} disabled={savingDetail}>Cerrar</Button>
    <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDetailSave} disabled={savingDetail}>
      Guardar cambios
    </Button>
  </DialogFooter>
</DialogContent>
</Dialog>
<Dialog open={Boolean(notesPreview)} onOpenChange={(open) => { if (!open && !savingNote) closeNotesPreview(); }}>
  <DialogContent className="max-w-2xl">
    <DialogHeader>
      <DialogTitle>Notas para {notesPreview ? formatOrderRef(notesPreview) : ''}</DialogTitle>
      <DialogDescription>Observaciones internas del pedido.</DialogDescription>
    </DialogHeader>
    <Textarea
      value={noteDraft}
      onChange={(e) => setNoteDraft(e.target.value)}
      rows={5}
      disabled={savingNote}
      placeholder="Observaciones para este pedido"
      className="min-h-[240px] max-h-[400px] resize-y overflow-x-hidden break-all whitespace-pre-wrap"
    />
    <DialogFooter>
      <Button variant="outline" onClick={closeNotesPreview} disabled={savingNote}>Cancelar</Button>
      <Button onClick={handleSaveNote} disabled={savingNote || noteDraft === (notesPreview?.notes ?? '')}>
        {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Guardar
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
      <OrderEmailDialog
        open={Boolean(orderPendingEmail)}
        order={orderPendingEmail}
        sending={sendingOrderEmail}
        onOpenChange={(open) => {
          if (!open && !sendingOrderEmail) setOrderPendingEmail(null);
        }}
        onSend={handleSendOrderEmail}
      />
    </div>
  );
}
